import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { IMG } from "@/lib/images";
import { Chapter, MaskedLines, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import LeadForm from "@/components/LeadForm";
import ServiceCard from "@/components/ServiceCard";
import {
  IDEA_TO_COMPLETION, MARQUEE_ITEMS, PHONE_DISPLAY, PHONE_TEL,
  SERVICE_AREA_CITIES, SERVICES, WHY_ITEMS, usePageMeta,
} from "@/lib/site";

const HERO_STATS = [
  ["30+ Years", "Combined construction experience"],
  ["Salt Lake City", "Serving communities across Utah"],
  ["Residential & Commercial", "From concept to completion"],
];

const PITCH_POINTS = [
  { n: "01", t: "Build", d: "Create the addition, garage, living space, or renovation your property needs." },
  { n: "02", t: "Leverage", d: "Explore ways your existing home equity may help fund the project." },
  { n: "03", t: "Create Value", d: "Design improvements around usable space, functionality, and long-term property value." },
];


export default function Home() {
  usePageMeta(
    "Salt Lake City General Contractor",
    "Black Sky Builders is a Salt Lake City construction company for home additions, garages, ADU-style spaces, renovations, concrete, and commercial improvements across Utah."
  );
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="home-hero" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <motion.img
            src={IMG.hero}
            alt="Active construction framing on a Utah job site"
            initial={{ scale: 1.18, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.5 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="blueprint-grid absolute inset-0 opacity-25" />

        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-4 pb-14 pt-36 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-silver"
          >
            Black Sky Builders — Salt Lake City, Utah
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="mt-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-fog sm:text-base"
          >
            "The sky isn't the limit, it's the beginning."
          </motion.p>
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["Turn Your Property", "Into More."]} baseDelay={0.4} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] uppercase tracking-[0.25em] text-fog"
          >
            {["Construction", "Renovation", "Equitable Construction", "Project Solutions"].map((w, i) => (
              <span key={w} className="flex items-center gap-4">
                {i > 0 && <span className="h-1 w-1 rotate-45 bg-steel" />}
                {w}
              </span>
            ))}
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-5 max-w-2xl text-sm leading-relaxed text-mist sm:text-base"
          >
            We help you evaluate the project, understand the numbers, explore financing, navigate permits,
            and build it through completion.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Link to="/contact" data-testid="hero-cta-consultation" className="btn-primary">
              Request a Free Project Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="hero-cta-discuss" className="btn-outline">
              Discuss Your Project
            </Link>
            <a href={PHONE_TEL} data-testid="hero-cta-call" className="link-arrow px-2 py-4">
              <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
            </a>
          </motion.div>
        </motion.div>

        <div className="relative border-t border-line/60 bg-ink/60 backdrop-blur-sm">
          <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-line/60 px-4 sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-8 lg:px-16">
            {HERO_STATS.map(([a, b], i) => (
              <motion.div
                key={a}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 + i * 0.12, duration: 0.6 }}
                className="py-5 sm:px-6 sm:first:pl-0"
              >
                <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{a}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-fog">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />


      {/* 01 THE BLACK SKY DIFFERENCE */}
      <section className="border-b border-line bg-coal" data-testid="home-pitch">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-2 lg:items-center lg:px-16 lg:py-28">
          <div>
            <Chapter
              index="01"
              eyebrow="Why Black Sky Is Different"
              title="Build more with the equity you already have"
              copy="Your home may already contain the opportunity to create the space you need."
            />
            <div className="-mt-4 space-y-5">
              <Reveal>
                <p className="text-sm leading-relaxed text-mist sm:text-base">
                  Black Sky Builders helps homeowners evaluate additions, garage apartments, ADU-style spaces,
                  renovations, and other strategic improvements through our Equitable Construction approach.
                </p>
              </Reveal>
              <Reveal delay={0.06}>
                <p className="text-sm leading-relaxed text-mist sm:text-base">
                  We look at the project as a whole: what you want to build, how it can improve the property,
                  what it may add in usable space and long-term value, and how the project can be funded.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="text-sm leading-relaxed text-mist sm:text-base">
                  For qualified homeowners, we can also help explore financing options that may allow you to leverage
                  available home equity to complete your project while minimizing the amount of cash required upfront.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="text-sm leading-relaxed text-mist sm:text-base">
                  From project planning and financing resources to design coordination, permits, construction, and
                  completion, Black Sky Builders helps you understand the path forward before you commit to the build.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <p className="font-display text-base font-bold uppercase tracking-wide sm:text-lg">
                  Let us show you what may be possible with your property.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <Link to="/contact" data-testid="home-pitch-cta" className="btn-primary mt-2">
                  Schedule a Free Project Consultation <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
              <Reveal delay={0.34}>
                <p className="font-mono text-[11px] leading-relaxed tracking-wide text-steel">
                  Black Sky Builders is not a lender. Financing is provided by independent financing resources
                  and is never guaranteed.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="flex flex-col gap-px self-center border border-line bg-line">
            {PITCH_POINTS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="flex h-full items-start gap-6 bg-ink p-8">
                  <span className="font-mono text-2xl font-bold tracking-[0.1em] text-steel">{p.n}</span>
                  <div>
                    <h3 className="font-display text-xl font-black uppercase tracking-tight sm:text-2xl">{p.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-fog">{p.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 02 WHAT WE BUILD */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16 lg:py-28" data-testid="home-services">
        <Chapter
          index="02"
          eyebrow="What We Build"
          title="Construction, handled end to end"
          copy="From home additions and garage apartments to commercial improvements and concrete — every project is planned, coordinated, and managed by an experienced team."
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.slice(0, 6).map((s, i) => (
            <ServiceCard key={s.num} s={s} i={i} />
          ))}
        </div>
        <Reveal className="mt-10">
          <Link to="/services" data-testid="home-all-services-link" className="btn-outline">
            View All Services <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* 02 EQUITABLE CONSTRUCTION */}
      <section className="border-y border-line bg-coal" data-testid="home-equitable">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-2 lg:items-center lg:px-16 lg:py-28">
          <div>
            <Chapter
              index="03"
              eyebrow="Equitable Construction"
              title="Build smarter. Not just bigger."
              copy="Equitable Construction is our strategic approach to building — looking beyond simply completing a project to how each addition, garage, living space, or renovation can increase usable space, improve functionality, and contribute meaningful long-term value to your property."
            />
            <div className="-mt-4 grid gap-px border border-line bg-line sm:grid-cols-3">
              {[
                ["More Usable Space", "Put every square foot of your property to work."],
                ["Better Functionality", "Spaces designed around how you actually live."],
                ["Long-Term Value", "Improvements made with the future in mind."],
              ].map(([t, d], i) => (
                <Reveal key={t} delay={i * 0.08} className="h-full">
                  <div className="h-full bg-coal p-5">
                    <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{t}</p>
                    <p className="mt-2 text-xs leading-relaxed text-fog">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15} className="mt-8">
              <Link to="/equitable-construction" data-testid="home-equitable-link" className="btn-primary">
                Explore Equitable Construction <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 font-mono text-[11px] leading-relaxed tracking-wide text-steel">
                A construction philosophy — not a promise of appraisal results, equity amounts, or investment returns.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="border border-line bg-ink p-3">
                <img src={IMG.addition} alt="Modern home addition exterior" loading="lazy" className="h-[320px] w-full object-cover grayscale sm:h-[460px]" />
              </div>
              <div className="absolute -bottom-5 left-6 border border-line bg-ink px-5 py-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fog">The Black Sky Approach</p>
                <p className="mt-1 font-display text-base font-bold uppercase tracking-wide sm:text-lg">Idea → Plan → Build → Value</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 03 FROM IDEA TO COMPLETION */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16 lg:py-28" data-testid="home-idea-to-completion">
        <Chapter
          index="04"
          eyebrow="From Idea to Completion"
          title="You bring the idea. We help build the path forward."
          copy="You don't need to figure out cost, financing, permits, plans, or engineering before you call. Depending on your project, our team can assist with every step between the first conversation and the final walkthrough."
        />
        <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {IDEA_TO_COMPLETION.map((item, i) => (
            <Reveal key={item} delay={(i % 3) * 0.06} className="h-full">
              <div className="flex h-full items-center gap-4 bg-ink p-5">
                <span className="font-mono text-xs tracking-[0.2em] text-steel">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm text-mist">{item}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="max-w-2xl text-sm leading-relaxed text-fog">
            Have an idea but unsure about cost, financing, permits, plans, engineering, or where to begin? That's
            exactly when to call.
          </p>
          <Link to="/contact" data-testid="home-idea-cta" className="btn-primary mt-6">
            Request a Project Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* 04 PROJECT FINANCING TEASER */}
      <section className="border-y border-line bg-coal" data-testid="home-financing">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-2 lg:items-center lg:px-16 lg:py-28">
          <Reveal className="order-2 lg:order-1">
            <div className="relative">
              <div className="border border-line bg-ink p-3">
                <img src={IMG.blueprint} alt="Architectural plans and project budgeting" loading="lazy" className="h-[320px] w-full object-cover grayscale sm:h-[420px]" />
              </div>
              <div className="absolute -top-5 right-6 border border-line bg-ink px-5 py-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fog">Plan First</p>
                <p className="mt-1 font-display text-base font-bold uppercase tracking-wide">Scope. Budget. Path.</p>
              </div>
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Chapter
              index="05"
              eyebrow="Project Financing"
              title="A better project starts with a better plan"
              copy="Planning a major construction project also means determining how it will be funded. For qualified projects, Black Sky Builders can help you explore construction financing resources and better understand potential funding paths while the project scope, budget, and construction plan are being developed."
            />
            <div className="-mt-4 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact?interest=financing" data-testid="home-financing-ask-button" className="btn-primary">
                Ask About Project Financing <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/financing" data-testid="home-financing-explore-link" className="btn-outline">
                Explore Financing
              </Link>
            </div>
            <Reveal delay={0.15}>
              <p className="mt-6 font-mono text-[11px] leading-relaxed tracking-wide text-steel">
                Black Sky Builders is not a lender. We do not guarantee financing, approvals, rates, property values,
                or lending terms.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 05 WHY BLACK SKY BUILDERS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16 lg:py-28" data-testid="home-why">
        <Chapter
          index="06"
          eyebrow="Why Black Sky Builders"
          title="We understand construction. We understand the numbers."
          copy="And we understand that communication matters just as much as the finished product. Here's what that looks like in practice."
        />
        <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {WHY_ITEMS.map((item, i) => (
            <Reveal key={item.t} delay={(i % 3) * 0.06} className="h-full">
              <div className="h-full bg-ink p-6">
                <span className="font-mono text-xs tracking-[0.3em] text-steel">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-base font-bold uppercase tracking-wide">{item.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fog">{item.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 09 CONSULTATION */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16 lg:py-28" data-testid="home-consultation">
        <Chapter
          index="07"
          eyebrow="Request a Consultation"
          title="Tell us what you want to build"
          copy="Whether you already have plans or are still figuring out where to begin, tell us about your project and our team can help determine the next steps."
        />
        <div className="max-w-4xl">
          <LeadForm />
        </div>
      </section>

      {/* 10 SERVICE AREA */}
      <section className="relative overflow-hidden border-t border-line" data-testid="home-service-area">
        <div className="absolute inset-0">
          <img src={IMG.mountains} alt="" loading="lazy" className="animate-slow-pan h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16 lg:py-28">
          <Chapter
            index="08"
            eyebrow="Service Area"
            title="Salt Lake City & surrounding Utah communities"
            copy="Black Sky Builders serves Salt Lake City and surrounding Utah communities, with the ability to evaluate projects throughout Utah."
          />
          <div className="flex flex-wrap gap-2">
            {SERVICE_AREA_CITIES.map((city, i) => (
              <Reveal key={city} delay={i * 0.03}>
                <span className="inline-block border border-line bg-ink/70 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist backdrop-blur-sm">
                  {city}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <p className="max-w-2xl text-sm leading-relaxed text-fog">
              Outside these areas? We evaluate projects throughout Utah — tell us where you're building.
            </p>
            <Link to="/contact" data-testid="home-area-cta" className="btn-primary mt-6">
              Request a Project Consultation <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
